// A run's Pre-sales tab (?rt=presales, ERP-PLAN §5.14): what's reserved for backers (Kickstarter, PledgeBox, pre-orders)
// and held (gifts, influencers) per item against what the run makes, shipped and remaining, what's left for retail; the
// money in (payouts as paid out, fees, refunds) with their statements; shipments to backers by month; and the result.
// Counts and totals only: no backer is ever named here. Every number explains itself on hover (Why). The math is
// shared/presales.ts, the same as the MCP's get_presale and the bookkeeper pack.
import { AlertTriangle, Gift, PackageCheck, Plus, Trash2, Truck } from "lucide-react";
import { type ReactNode, useRef, useState } from "react";
import { numberOf } from "../../../../../shared/calc.ts";
import { planToday } from "../../../../../shared/day.ts";
import type { Presale, PresaleIncome, ProductionCost, ProductionRunHold } from "../../../../../shared/db.gen.ts";
import {
  CHANNEL_LABELS,
  INCOME_KIND_HINTS,
  INCOME_KIND_LABELS,
  INCOME_KINDS,
  INCOME_OUT,
  INCOME_SOURCE_LABELS,
  INCOME_SOURCES,
  type IncomeKind,
  incomeCents,
  PRESALE_CHANNELS,
  PRESALE_FILE_HINT,
  PRESALE_STATUS_HINTS,
  PRESALE_STATUS_LABELS,
  PRESALE_STATUS_TONE,
  PRESALE_STATUSES,
  type PresaleChannel,
  type PresaleState,
  presaleResult,
  REGION_LABELS,
  REGIONS,
  type Region,
  type RunItemRow,
  runPresales,
  SHIPPING_KIND_LABELS,
  SHIPPING_KINDS,
  SHIPS_FROM_LABELS,
  type ShipmentState,
  type ShippingKind,
} from "../../../../../shared/presales.ts";
import { centsText, shortDate, useCostIndex } from "../../../lib/costs.ts";
import { qty } from "../../../lib/format.ts";
import { can, mutate, mutateWithResult } from "../../../lib/mutate.ts";
import { usePresaleIndex } from "../../../lib/presales.ts";
import { useRows } from "../../../lib/sync.ts";
import { Attachments } from "../../../ui/Attachments.tsx";
import { Badge, Button, cx, Input, Select, Textarea, UpperInput } from "../../../ui/controls.tsx";
import { type Column, DataTable, type TableSection } from "../../../ui/DataTable.tsx";
import { DialogLayer } from "../../../ui/DialogLayer.tsx";
import { toast } from "../../../ui/toast.tsx";
import { Why } from "../../../ui/Why.tsx";
import { Card } from "../Workspace.tsx";
import type { RunProps } from "./RunWorkspace.tsx";

const n = (v: unknown) => Number(v ?? 0) || 0;

/** A number with its why, right-aligned and mono, blank (a faint dash) when 0 unless `zero`. */
function Num({ v, why, title, tone, zero }: { v: number; why?: string; title?: string; tone?: "danger" | "warn" | "ok" | "muted"; zero?: boolean }) {
  const text = v === 0 && !zero ? <span className="text-faint">—</span> : qty(v);
  return (
    <span
      className={cx(
        "text-right font-mono text-[12px]",
        tone === "danger" && "font-semibold text-danger",
        tone === "warn" && "text-warn",
        tone === "ok" && "text-ok",
        tone === "muted" && "text-muted",
      )}
    >
      {why || title ? (
        <Why title={title} note={why} width={340}>
          {text}
        </Why>
      ) : (
        text
      )}
    </span>
  );
}

export function RunPresalesTab({ run }: RunProps) {
  const px = usePresaleIndex();
  const rp = runPresales(px, run.id);
  const [dialog, setDialog] = useState<
    | { kind: "presale"; id: string | null }
    | { kind: "line"; presaleId: string; itemId: string | null }
    | { kind: "hold"; id: string | null; itemId?: string }
    | { kind: "income"; presaleId: string; id: string | null }
    | { kind: "shipment"; presaleId: string }
    | null
  >(null);
  const editable = can("presale.update");
  const close = () => setDialog(null);
  // With several pre-sales on the run: an item opens the one that reserves it (else the first that isn't cancelled), and
  // the dialog says which, with a choice.
  const live = rp.presales.filter((s) => s.presale.status !== "cancelled");
  const first = live[0] ?? rp.presales[0];
  const presaleFor = (itemId: string | null) =>
    (itemId
      ? (live.find((s) => s.lines.some((l) => l.item_id === itemId)) ?? rp.presales.find((s) => s.lines.some((l) => l.item_id === itemId)))
      : undefined) ?? first;
  if (!rp.presales.length && !rp.holds.length)
    return (
      <>
        <Card title="Pre-sales" note="Units of this run sold before it's built (Kickstarter, PledgeBox, pre-orders, wholesale), and units held back">
          <div className="flex flex-col items-center gap-2 px-4 py-8 text-center text-muted">
            <PackageCheck className="size-6 text-faint" />
            <div className="max-w-xl">
              Nothing pre-sold or held on {run.number}. A pre-sale reserves units per item for backers: they ship from the CM and never count as stock coming
              for sale. Load one from the backers app with{" "}
              <code className="rounded bg-surface-2 px-1 font-mono text-[11px]">presale-import {run.number} summary.json</code>, or add it here.
            </div>
            {editable && (
              <div className="mt-1 flex gap-2">
                <Button variant="primary" size="sm" icon={<Plus className="size-3.5" />} onClick={() => setDialog({ kind: "presale", id: null })}>
                  Add a pre-sale
                </Button>
                <Button size="sm" icon={<Gift className="size-3.5" />} onClick={() => setDialog({ kind: "hold", id: null })}>
                  Hold units
                </Button>
              </div>
            )}
          </div>
        </Card>
        {dialog?.kind === "presale" && <PresaleDialog runId={run.id} id={null} onClose={close} />}
        {dialog?.kind === "hold" && <HoldDialog runId={run.id} id={null} rows={rp.rows} onClose={close} />}
      </>
    );
  return (
    <>
      {rp.presales.map((s) => (
        <PresaleHeader key={s.presale.id} s={s} editable={editable} onEdit={() => setDialog({ kind: "presale", id: s.presale.id })} />
      ))}
      {rp.flags.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-danger/40 bg-danger-soft/40 px-4 py-2.5 text-[12px]">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-danger" />
          <div>
            <div className="font-medium text-danger">Needs a look</div>
            {rp.flags.map((f) => (
              <div key={f}>{f}</div>
            ))}
          </div>
        </div>
      )}
      <ReservationsCard
        rows={rp.rows}
        runNumber={run.number}
        editable={editable}
        onLine={(itemId) => {
          const s = presaleFor(itemId);
          if (s) setDialog({ kind: "line", presaleId: s.presale.id, itemId });
        }}
        onHold={(id, itemId) => setDialog({ kind: "hold", id, itemId })}
        onAdd={() => first && setDialog({ kind: "line", presaleId: first.presale.id, itemId: null })}
        onAddHold={() => setDialog({ kind: "hold", id: null })}
        canAddLine={!!first}
      />
      {rp.presales.map((s) => (
        <div key={s.presale.id} className="grid grid-cols-1 gap-3 xl:grid-cols-2">
          <ResultCard presaleId={s.presale.id} />
          <IncomeCard s={s} editable={editable} onOpen={(id) => setDialog({ kind: "income", presaleId: s.presale.id, id })} />
          <ShipmentsCard s={s} editable={editable} onAdd={() => setDialog({ kind: "shipment", presaleId: s.presale.id })} className="xl:col-span-2" />
        </div>
      ))}
      {editable && (
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" icon={<Plus className="size-3.5" />} onClick={() => setDialog({ kind: "presale", id: null })}>
            Another pre-sale on {run.number}
          </Button>
        </div>
      )}
      {dialog?.kind === "presale" && <PresaleDialog runId={run.id} id={dialog.id} onClose={close} />}
      {dialog?.kind === "line" && (
        <LineDialog presaleId={dialog.presaleId} presales={rp.presales.map((s) => s.presale)} itemId={dialog.itemId} runId={run.id} onClose={close} />
      )}
      {dialog?.kind === "hold" && <HoldDialog runId={run.id} id={dialog.id} itemId={dialog.itemId} rows={rp.rows} onClose={close} />}
      {dialog?.kind === "income" && <IncomeDialog presaleId={dialog.presaleId} id={dialog.id} onClose={close} />}
      {dialog?.kind === "shipment" && <ShipmentDialog presaleId={dialog.presaleId} rows={rp.rows} onClose={close} />}
    </>
  );
}

function PresaleHeader({ s, editable, onEdit }: { s: PresaleState; editable: boolean; onEdit: () => void }) {
  const p = s.presale;
  return (
    // The pre-sale's heading: a line of its own above its sections, not a box
    <section className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-1">
      <div className="flex items-center gap-2">
        <h2 className="text-[15px] font-semibold">{p.name}</h2>
        <Why title={PRESALE_STATUS_LABELS[p.status]} note={PRESALE_STATUS_HINTS[p.status]}>
          <Badge tone={PRESALE_STATUS_TONE[p.status]}>{PRESALE_STATUS_LABELS[p.status]}</Badge>
        </Why>
        {p.channels.map((c) => (
          <Badge key={c}>{CHANNEL_LABELS[c as PresaleChannel] ?? c}</Badge>
        ))}
      </div>
      <Why title="Where it ships from" note={SHIPS_FROM_LABELS[p.ships_from]}>
        <span className="text-[12px] text-muted">Ships from {p.ships_from === "cm" ? "the CM" : "HHL"}</span>
      </Why>
      <Why
        title={`${qty(s.boxes)} backer boxes`}
        lines={[
          { label: "US (ship from Philadelphia)", value: qty(n(p.backers_us)) },
          { label: "International (DDP forwarder)", value: qty(n(p.backers_intl)) },
          { label: "No country yet", value: qty(n(p.backers_unknown)) },
        ]}
        note="From the backers app's summary: counts only, never a name or an address."
      >
        <span className="text-[12px]">
          <span className="font-mono">{qty(s.boxes)}</span> <span className="text-muted">backers</span>
        </span>
      </Why>
      <Why title="Counts as of" note="The date of the backers app's export these counts come from. A new import replaces them; shipments stay.">
        <span className="text-[12px] text-muted">counts as of {p.as_of ? shortDate(String(p.as_of)) : "—"}</span>
      </Why>
      {p.order_tag && (
        <Why title="Shopify order tag" note={`Shopify orders tagged ${p.order_tag} (the backers' US orders) aren't counted as sales in the forecast.`}>
          <span className="font-mono text-[11px] text-muted">#{p.order_tag}</span>
        </Why>
      )}
      {editable && (
        <Button size="sm" variant="ghost" className="ml-auto" onClick={onEdit}>
          Edit
        </Button>
      )}
    </section>
  );
}

/** A row of the reservations table: an item, or one of its holds (in the Held band). */
type ResRow = { id: string; r: RunItemRow; hold: ProductionRunHold | null };

const ROLE_BANDS: Record<RunItemRow["role"], { title: string; note: string; order: number }> = {
  made: { title: "What the run makes", note: "Backers' units come off the build at the CM and never come to HHL", order: 0 },
  bought: { title: "Bought for it", note: "Add-ons and run goods packed into backers' boxes at the CM", order: 1 },
  neither: { title: "Not on this run yet", note: "Reserved, but the run neither makes nor buys it", order: 2 },
};
const HOLD_NOTE = "Arrives at HHL as usual, never planned as for sale. Lower or remove the hold to release them.";
const bandLabel = (title: string, note: string) => (
  <>
    {title}
    <span className="font-normal text-faint normal-case">· {note}</span>
  </>
);
const roleBand = (key: RunItemRow["role"]): TableSection => ({
  key,
  order: ROLE_BANDS[key].order,
  label: bandLabel(ROLE_BANDS[key].title, ROLE_BANDS[key].note),
});
const RES_BANDS: Record<RunItemRow["role"] | "held", TableSection> = {
  made: roleBand("made"),
  bought: roleBand("bought"),
  neither: roleBand("neither"),
  held: { key: "held", order: 3, label: bandLabel("Held", HOLD_NOTE) },
};

function ReservationsCard({
  rows: items,
  runNumber,
  editable,
  onLine,
  onHold,
  onAdd,
  onAddHold,
  canAddLine,
}: {
  rows: RunItemRow[];
  runNumber: string;
  editable: boolean;
  onLine: (itemId: string) => void;
  onHold: (id: string, itemId: string) => void;
  onAdd: () => void;
  onAddHold: () => void;
  canAddLine: boolean;
}) {
  const rows: ResRow[] = [
    ...items.map((r) => ({ id: r.itemId, r, hold: null })),
    ...items.flatMap((r) => r.holds.map((h) => ({ id: `hold:${h.id}`, r, hold: h }))),
  ];
  // The totals line is what the run makes, in all (made items only), as before.
  const made = (rs: ResRow[]) => rs.filter((x) => !x.hold && x.r.role === "made").map((x) => x.r);
  const anyMade = items.some((r) => r.role === "made");
  const sum = (rs: ResRow[], k: keyof RunItemRow) => made(rs).reduce((t, r) => t + n(r[k]), 0);
  const total = (k: keyof RunItemRow, why: string, opts: { zero?: boolean; tone?: (v: number) => "danger" | "ok" | undefined } = {}) =>
    anyMade
      ? (rs: ResRow[]) => {
          const v = sum(rs, k);
          return <Num v={v} zero={opts.zero} why={why} tone={opts.tone?.(v)} />;
        }
      : undefined;
  const short = (r: RunItemRow) => Math.max(r.reserved, r.shipped) + r.held > r.made;
  // A number of an item's row (a hold's row leaves it blank).
  const num = (
    k: keyof RunItemRow,
    header: string,
    cell: (r: RunItemRow) => ReactNode,
    sumWhy: string,
    extra: Partial<Column<ResRow>> = {},
  ): Column<ResRow> => ({
    key: k,
    header,
    width: "76px",
    align: "right",
    value: (x) => (x.hold ? null : n(x.r[k])),
    cell: (x) => (x.hold ? null : cell(x.r)),
    total: total(k, sumWhy),
    ...extra,
  });
  const columns: Column<ResRow>[] = [
    {
      key: "sku",
      header: "SKU",
      width: "minmax(120px,1fr)",
      value: (x) => x.r.sku,
      cell: (x) => <span className={cx("truncate font-mono text-[12px]", x.hold && "text-muted")}>{x.hold ? `↳ ${x.r.sku}` : x.r.sku}</span>,
      total: anyMade ? () => null : undefined, // the totals line's words are under Name, which has the room
    },
    {
      key: "name",
      header: "Name",
      width: "minmax(140px,1.4fr)",
      value: (x) => (x.hold ? `Held ${x.hold.reason}` : x.r.name),
      cell: (x) =>
        x.hold ? (
          <span className="flex min-w-0 items-center gap-2" title={HOLD_NOTE}>
            <Badge tone="warn">Held</Badge>
            <span className="truncate text-[12px] text-muted">{x.hold.reason || "No reason given"}</span>
          </span>
        ) : (
          <span className="truncate text-[12px] text-muted">{x.r.name}</span>
        ),
      total: anyMade ? () => <span className="truncate">What the run makes, in all</span> : undefined,
    },
    {
      key: "made",
      header: "The run makes",
      width: "100px",
      align: "right",
      value: (x) => (x.hold ? null : x.r.made),
      cell: ({ r, hold }) =>
        hold ? null : (
          <Why
            title={`What ${runNumber} ${r.role === "bought" ? "buys" : "makes"}`}
            lines={
              r.makeLines.length
                ? r.makeLines.map((l) => ({
                    label: l.po,
                    value: qty(l.qty),
                    detail: l.direct
                      ? `${qty(l.direct)} go straight to backers, ${qty(l.forHhl)} come to HHL (${qty(l.received)} received)`
                      : `all ${qty(l.qty)} come to HHL`,
                  }))
                : undefined
            }
            note={r.madeText}
          >
            <span className={cx("font-mono text-[12px]", short(r) && "font-semibold text-danger")}>{qty(r.made)}</span>
          </Why>
        ),
      total: total("made", "Units on the run's make PO lines (else its plan), every variant", { zero: true }),
    },
    {
      key: "from",
      header: "From",
      headerTitle: "Where what the run makes or buys comes from: its make PO lines, its plan or its other POs",
      width: "minmax(130px,1fr)",
      value: (x) => (x.hold ? "" : x.r.madeText),
      cell: (x) => (x.hold ? null : <span className="truncate text-[11px] text-faint">{x.r.madeText}</span>),
    },
    num(
      "us",
      "US",
      (r) => <Num v={r.us} why="Reserved for US backers (ship from Philadelphia)" />,
      "Reserved for US backers: their boxes go to Philadelphia and ship from there",
    ),
    num(
      "intl",
      "Intl",
      (r) => <Num v={r.intl} why={`Reserved for international backers (DDP forwarder)${r.unknown ? `; ${qty(r.unknown)} more have no country yet` : ""}`} />,
      "Reserved for international backers: their boxes go to the DDP forwarder",
    ),
    num(
      "reserved",
      "Reserved",
      (r) => (
        <Num
          v={r.reserved}
          why={`${qty(r.us)} US + ${qty(r.intl)} international${r.unknown ? ` + ${qty(r.unknown)} no country yet` : ""}${r.notShipping ? `. Not counted: ${qty(r.notShipping)} not shipping (cancelled or dropped)` : ""}${r.direct ? `. ${qty(r.direct)} of them are set aside on the make PO: never expected at HHL` : ""}`}
          tone={short(r) ? "danger" : undefined}
        />
      ),
      "US + international + no country yet",
    ),
    num(
      "assigned",
      "Colours set",
      (r) => <Num v={r.assigned} why="Of the reserved: colours HHL chose for backers who didn't finish their survey" tone="muted" />,
      "Colours HHL chose for backers who didn't finish their survey",
      { width: "90px" },
    ),
    num(
      "shipped",
      "Shipped",
      (r) => <Num v={r.shipped} why="Sent to backers so far (shipments below)" tone={r.over ? "warn" : undefined} />,
      "Sent to backers so far (shipments below)",
    ),
    num(
      "remaining",
      "Remaining",
      (r) => <Num v={r.remaining} why={`Reserved − shipped, never below 0${r.over ? `. ${qty(r.over)} shipped beyond the reservation` : ""}`} />,
      "Reserved − shipped",
      { width: "90px" },
    ),
    {
      key: "held",
      header: "Held",
      width: "76px",
      align: "right",
      value: (x) => (x.hold ? n(x.hold.qty) : x.r.held),
      cell: (x) =>
        x.hold ? (
          <Num v={n(x.hold.qty)} zero why={`Held: ${x.hold.reason || "no reason given"}. Received into stock at HHL, kept out of what's for sale.`} />
        ) : (
          <Num v={x.r.held} why={x.r.holds.map((h) => `${qty(n(h.qty))}: ${h.reason || "held"}`).join("; ") || "Nothing held"} />
        ),
      total: total("held", "Held back: gifts, influencers, employees. They arrive at HHL but aren't for sale until released."),
    },
    {
      key: "retailLeft",
      header: "Retail left",
      width: "100px",
      align: "right",
      value: (x) => (x.hold ? null : x.r.retailLeft),
      cell: ({ r, hold }) =>
        hold ? null : (
          <>
            {r.flags.length > 0 && <AlertTriangle className="size-3.5 shrink-0 text-danger" aria-label={r.flags.join("; ")} />}
            <Num
              v={r.retailLeft}
              zero
              why={`${qty(r.made)} ${r.role === "bought" ? "bought" : "made"} − ${qty(Math.max(r.reserved, r.shipped))} ${r.shipped > r.reserved ? "shipped" : "reserved"} − ${qty(r.held)} held = ${qty(r.retailLeft)}${r.role === "bought" ? " left over" : " for retail"}${r.flags.length ? `. ${r.flags.join(". ")}` : ""}`}
              tone={r.retailLeft < 0 ? "danger" : r.flags.length ? "warn" : undefined}
            />
          </>
        ),
      total: total("retailLeft", "What's left to sell: made − reserved (or shipped, if more) − held", { zero: true, tone: (v) => (v < 0 ? "danger" : "ok") }),
    },
  ];
  return (
    <Card
      boxed={false}
      title="Reserved and held, per item"
      note="Reserved units are never counted as stock coming for sale; held ones arrive at HHL and stay out of it"
      actions={
        <>
          {editable && (
            <>
              <Button size="sm" icon={<Gift className="size-3.5" />} onClick={onAddHold}>
                Hold units
              </Button>
              {canAddLine && (
                <Button size="sm" icon={<Plus className="size-3.5" />} onClick={onAdd}>
                  Reserve an item
                </Button>
              )}
            </>
          )}
        </>
      }
    >
      <DataTable
        fit={20}
        rows={rows}
        columns={columns}
        sectionOf={(x) => RES_BANDS[x.hold ? "held" : x.r.role]}
        onOpen={editable ? (x) => (x.hold ? onHold(x.hold.id, x.r.itemId) : onLine(x.r.itemId)) : undefined}
        exportName="Reserved and held"
        pageFilters={false}
        rowNoun={["row", "rows"]}
      />
    </Card>
  );
}

function ResultCard({ presaleId }: { presaleId: string }) {
  const px = usePresaleIndex();
  const cx_ = useCostIndex();
  const res = presaleResult(cx_, px, presaleId);
  if (!res) return null;
  const line = (label: string, cents: number | null, why: string, opts: { strong?: boolean; sign?: "-" | "" } = {}) => (
    <div className={cx("flex items-baseline gap-2 px-4 py-1.5", opts.strong && "border-t border-line font-semibold")}>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <Why title={label} note={why} width={380}>
        <span className={cx("font-mono text-[12px]", opts.strong && cents != null && (cents < 0 ? "text-danger" : "text-ok"))}>
          {cents == null ? "—" : `${opts.sign === "-" && cents ? "− " : ""}${centsText(opts.sign === "-" ? Math.abs(cents) : cents)}`}
        </span>
      </Why>
    </div>
  );
  return (
    <Card
      title="Result"
      note={
        res.cancelled
          ? `${res.presale.name} is cancelled: income − what it shipped at landed cost − shipping to backers`
          : `${res.presale.name}: income − its units at landed cost − shipping to backers`
      }
    >
      <div className="py-1">
        {line("Money in (as paid out)", res.income.inCents, res.why.income)}
        {res.income.feesCents !== 0 && line("Fees taken separately", res.income.feesCents, res.why.fees, { sign: "-" })}
        {res.income.refundsCents !== 0 && line("Refunds", res.income.refundsCents, res.why.refunds, { sign: "-" })}
        {line("Net income", res.income.netCents, res.why.net, { strong: true })}
        <div className="flex items-baseline gap-2 px-4 py-1.5">
          <span className="min-w-0 flex-1 truncate">{qty(res.units.count)} units at landed cost</span>
          <Why
            title="Units at landed cost"
            lines={res.units.lines.map((l) => ({
              label: `${l.sku} × ${qty(l.units)}`,
              value: centsText(l.cents),
              detail: l.cost.text,
            }))}
            note={res.why.units}
            width={460}
          >
            <span className="font-mono text-[12px]">− {centsText(res.units.cents)}</span>
          </Why>
        </div>
        {res.units.missing.length > 0 && <div className="px-4 pb-1 text-[11px] text-warn">No cost yet for {res.units.missing.join(", ")}: counted as $0</div>}
        <div className="flex items-baseline gap-2 px-4 py-1.5">
          <span className="min-w-0 flex-1 truncate">Shipping to backers</span>
          <Why title="Shipping to backers" lines={res.shipping.byKind.map((k) => ({ label: k.label, value: centsText(k.cents) }))} note={res.why.shipping}>
            <span className="font-mono text-[12px]">− {centsText(res.shipping.cents)}</span>
          </Why>
        </div>
        {line("Result", res.resultCents, res.why.result, { strong: true })}
        <div className="grid grid-cols-3 gap-2 border-t border-line px-4 py-2 text-[12px]">
          <Stat label="Per backer box" value={res.perBoxCents == null ? "—" : centsText(res.perBoxCents)} why={res.why.perBox} />
          <Stat label="Per unit" value={res.perUnitCents == null ? "—" : centsText(res.perUnitCents)} why={res.why.perUnit} />
          <Stat label="Pledge value shipped" value={centsText(res.pledgeShippedCents)} why={res.why.pledge} />
        </div>
      </div>
    </Card>
  );
}

function Stat({ label, value, why }: { label: string; value: string; why: string }) {
  return (
    <div>
      <div className="text-[11px] text-faint">{label}</div>
      <Why title={label} note={why}>
        <span className="font-mono text-[13px] font-medium">{value}</span>
      </Why>
    </div>
  );
}

type IncomeRow = PresaleIncome & { cents: number };

function IncomeCard({ s, editable, onOpen }: { s: PresaleState; editable: boolean; onOpen: (id: string | null) => void }) {
  const totals = new Map<string, number>();
  for (const e of s.income) totals.set(e.source, (totals.get(e.source) ?? 0) + incomeCents(e));
  const all = s.income.reduce((t, e) => t + incomeCents(e), 0);
  const rows: IncomeRow[] = s.income.map((e) => ({ ...e, cents: incomeCents(e) }));
  const what = (e: IncomeRow) => `${INCOME_SOURCE_LABELS[e.source]} ${INCOME_KIND_LABELS[e.kind].toLowerCase()}`;
  const columns: Column<IncomeRow>[] = [
    {
      key: "received_on",
      header: "Date",
      width: "86px",
      value: (e) => String(e.received_on),
      cell: (e) => <span className="font-mono text-[12px] text-muted">{shortDate(String(e.received_on))}</span>,
    },
    { key: "what", header: "What", width: "minmax(130px,1fr)", value: what, cell: (e) => <span className="truncate">{what(e)}</span> },
    { key: "description", header: "Description", width: "minmax(110px,1fr)", cell: (e) => <span className="truncate text-muted">{e.description}</span> },
    { key: "reference", header: "Reference", width: "120px", cell: (e) => <span className="truncate text-[11px] text-faint">{e.reference}</span> },
    { key: "note", header: "Note", width: "minmax(100px,1fr)", optional: true, cell: (e) => <span className="truncate text-[11px] text-faint">{e.note}</span> },
    {
      key: "cents",
      header: "Amount",
      headerTitle: "In USD: in is plus, out (fees, refunds) is minus",
      width: "110px",
      align: "right",
      value: (e) => e.cents / 100,
      cell: (e) => (
        <Why
          title={INCOME_KIND_LABELS[e.kind]}
          note={`${INCOME_KIND_HINTS[e.kind]}${e.currency !== "USD" ? `. ${e.amount} ${e.currency} at ${e.usd_rate}` : ""}`}
        >
          <span className={cx("font-mono text-[12px]", e.cents < 0 && "text-danger")}>{centsText(e.cents)}</span>
        </Why>
      ),
    },
    {
      key: "files",
      header: "Files",
      width: "minmax(110px,160px)",
      value: () => "",
      cell: (e) => <Attachments owner={{ type: "presale_income", id: e.id }} compact disabled={!editable} hint={PRESALE_FILE_HINT} />,
    },
  ];
  return (
    <Card
      boxed={false}
      title="Income"
      note="As paid out (a payout's fees are already off it)"
      actions={
        <>
          {editable && (
            <Button size="sm" icon={<Plus className="size-3.5" />} onClick={() => onOpen(null)}>
              Add
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-wrap gap-x-4 gap-y-1 px-1 text-[12px]">
        {[...totals].map(([source, cents]) => (
          <Why key={source} title={INCOME_SOURCE_LABELS[source as keyof typeof INCOME_SOURCE_LABELS] ?? source} note="Its entries in USD: in less out">
            <span>
              <span className="text-muted">{INCOME_SOURCE_LABELS[source as keyof typeof INCOME_SOURCE_LABELS] ?? source}</span>{" "}
              <span className="font-mono">{centsText(cents)}</span>
            </span>
          </Why>
        ))}
        <Why title="Net income" note="Every entry in USD: payouts, shipping collected and other in; fees and refunds out">
          <span className="font-medium">
            Net <span className="font-mono">{centsText(all)}</span>
          </span>
        </Why>
      </div>
      <DataTable
        fit
        rows={rows}
        columns={columns}
        defaultSort={{ key: "received_on" }}
        onOpen={editable ? (e) => onOpen(e.id) : undefined}
        exportName="Pre-sale income"
        pageFilters={false}
        rowNoun={["entry", "entries"]}
        empty="No income entered yet."
      />
    </Card>
  );
}

type ShipmentRow = ShipmentState & { id: string; shipping: number; billed: boolean; costs: ProductionCost[] };

function ShipmentsCard({ s, editable, onAdd, className }: { s: PresaleState; editable: boolean; onAdd: () => void; className?: string }) {
  const px = usePresaleIndex();
  const cx_ = useCostIndex();
  const months = new Map<string, typeof s.shipments>();
  for (const x of s.shipments) months.set(x.month, [...(months.get(x.month) ?? []), x]);
  const costsOf = (shipmentId: string) => (cx_.costsByRun.get(s.presale.run_id) ?? []).filter((c) => c.presale_shipment_id === shipmentId);
  const usdOf = (c: { amount: number; currency: string; usd_rate: number }) =>
    Math.round((c.currency.toUpperCase() === "USD" ? n(c.amount) : n(c.amount) * n(c.usd_rate)) * 100);
  // Shipping costs already on a bill (not to be booked twice): their bill numbers.
  const bills = useRows("bill");
  const billLines = useRows("bill_line");
  const billsOf = (costId: string) => [
    ...new Set(
      billLines
        .filter((l) => l.production_cost_id === costId)
        .flatMap((l) => bills.filter((b) => b.id === l.bill_id && b.status !== "void").map((b) => b.number)),
    ),
  ];
  // Recorded before a cost (or the total pledge value) was known: fill them in now (never changes one that has it).
  const missing =
    s.shipments.reduce((t, x) => t + x.lines.filter((l) => l.unit_cost == null).length, 0) +
    (s.presale.pledge_total != null && Number(s.presale.pledge_total) > 0
      ? s.shipments.filter((x) => x.shipment.deposit_ratio == null || Number(x.shipment.deposit_ratio) === 0).length
      : 0);
  const fill = async () => {
    const r = await mutateWithResult("presale_shipment.fill_costs", { presale_id: s.presale.id });
    const res = r.result as { lines: number; shipments: number; still: number } | undefined;
    if (r.ok && res)
      toast.ok(
        `Filled in ${res.lines} unit ${res.lines === 1 ? "cost" : "costs"}${res.shipments ? ` and ${res.shipments} ${res.shipments === 1 ? "share" : "shares"} of the deposits` : ""}${res.still ? `; ${res.still} still have no cost` : ""}`,
      );
  };
  const remove = async (id: string) => {
    if (!confirm("Take this shipment back? Its units return to the CM's book and its shipping costs are removed.")) return;
    const r = await mutateWithResult("presale_shipment.remove", { id });
    // What it told staff to take off in Shopify (add-ons at a Shopify CM location) goes back on.
    for (const note of (r.result as { notes?: string[] } | undefined)?.notes ?? []) toast.info(note);
  };
  // A band per month: its name, units and pledge value.
  const bands = new Map(
    [...months].map(([month, list], i): [string, TableSection] => {
      const units = list.reduce((t, x) => t + x.units, 0);
      const pledge = list.reduce((t, x) => t + n(x.shipment.pledge_value), 0);
      return [
        month,
        {
          key: month,
          order: i,
          label: (
            <>
              {new Date(`${month}-15T12:00:00Z`).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
              <span className="font-normal text-faint normal-case">
                {qty(units)} units · pledge value {centsText(Math.round(pledge * 100))}
              </span>
            </>
          ),
        },
      ];
    }),
  );
  const rows: ShipmentRow[] = s.shipments.map((x) => {
    const costs = costsOf(x.shipment.id);
    return { ...x, id: x.shipment.id, costs, shipping: costs.reduce((t, c) => t + usdOf(c), 0), billed: costs.some((c) => billsOf(c.id).length > 0) };
  });
  const columns: Column<ShipmentRow>[] = [
    {
      key: "shipped_on",
      header: "Shipped",
      width: "100px",
      value: (x) => String(x.shipment.shipped_on),
      cell: (x) => <span className="font-mono text-[12px]">{shortDate(String(x.shipment.shipped_on))}</span>,
    },
    {
      key: "region",
      header: "Region",
      width: "120px",
      value: (x) => REGION_LABELS[x.shipment.region],
      cell: (x) => <span className="text-[12px]">{REGION_LABELS[x.shipment.region]}</span>,
    },
    {
      key: "packages",
      header: "Packages",
      width: "90px",
      align: "right",
      value: (x) => n(x.shipment.packages),
      cell: (x) => <Num v={n(x.shipment.packages)} why="Packages (backer boxes) in it" />,
    },
    {
      key: "units",
      header: "Units",
      width: "80px",
      align: "right",
      cell: (x) => (
        <span className="font-mono text-[12px]">
          <Why
            title="Units shipped"
            lines={x.lines.map((l) => ({
              label: px.itemById.get(l.item_id)?.sku ?? "",
              value: qty(n(l.qty)),
              detail:
                l.unit_cost == null
                  ? "No landed cost when it was recorded"
                  : `${centsText(Math.round(n(l.unit_cost) * 100))} each at landed cost when recorded`,
            }))}
            note="They left the CM: finished goods out of 'at CM, finished', local parts out of its ledger. The bookkeeper pack keeps each line's landed cost from when it was recorded."
          >
            {qty(x.units)}
          </Why>
        </span>
      ),
    },
    {
      key: "pledge",
      header: "Pledge value",
      width: "110px",
      align: "right",
      value: (x) => n(x.shipment.pledge_value),
      cell: (x) => (
        <Why
          title="Pledge value"
          note="What the backers shipped had pledged (USD, total only), before the platforms' fees: for reference. The bookkeeper pack's share of deposits is the amount to recognise"
        >
          <span className="font-mono text-[12px]">{centsText(Math.round(n(x.shipment.pledge_value) * 100))}</span>
        </Why>
      ),
    },
    {
      key: "shipping",
      header: "Shipping",
      width: "120px",
      align: "right",
      value: (x) => x.shipping / 100,
      cell: (x) => (
        <Why
          title="Shipping costs"
          lines={x.costs.map((c) => {
            const on = billsOf(c.id);
            return {
              label: SHIPPING_KIND_LABELS[(c.shipping_kind || "other") as ShippingKind],
              value: centsText(usdOf(c)),
              ...(on.length ? { detail: `On ${on.join(", ")}: don't book it twice` } : {}),
            };
          })}
          note="Run costs of category Shipping to backers: in the pre-sale's result and the run's spend, never in a unit's cost"
        >
          <span className="font-mono text-[12px]">
            {centsText(x.shipping)}
            {x.billed && <span className="ml-1 font-sans text-[10px] text-muted">billed</span>}
          </span>
        </Why>
      ),
    },
    {
      key: "note",
      header: "Note",
      width: "minmax(120px,1fr)",
      value: (x) => x.shipment.note,
      cell: (x) => <span className="truncate text-[12px] text-muted">{x.shipment.note}</span>,
    },
    ...(editable
      ? [
          {
            key: "remove",
            header: "",
            width: "40px",
            value: () => "",
            cell: (x: ShipmentRow) => (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(x.shipment.id);
                }}
                className="text-faint hover:text-danger"
                aria-label="Take it back"
                title="Take it back"
              >
                <Trash2 className="size-3.5" />
              </button>
            ),
          },
        ]
      : []),
  ];
  return (
    <Card
      boxed={false}
      className={className}
      title="Shipments to backers"
      note="A dated total per region, usually a month's: what moves from customer deposits to income is its pledge value"
      actions={
        <>
          {editable && (
            <>
              {missing > 0 && can("presale_shipment.fill_costs") && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={fill}
                  title="Shipments recorded before a unit's landed cost (or the total pledge value) was known: fill them in with today's. Lines that have one never change; the bookkeeper pack says when it was filled in."
                >
                  Fill in missing unit costs
                </Button>
              )}
              <Button size="sm" icon={<Truck className="size-3.5" />} onClick={onAdd}>
                Record a shipment
              </Button>
            </>
          )}
        </>
      }
    >
      <DataTable
        fit
        rows={rows}
        columns={columns}
        sectionOf={(x) => bands.get(x.month) ?? null}
        defaultSort={{ key: "shipped_on" }}
        exportName="Backer shipments"
        pageFilters={false}
        rowNoun={["shipment", "shipments"]}
        empty="Nothing shipped to backers yet."
      />
    </Card>
  );
}

// Dialogs ------------------------------------------------------------------------------------------------------------

function Dialog({ title, note, children, onClose, width = 560 }: { title: string; note?: string; children: ReactNode; onClose: () => void; width?: number }) {
  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rounded-xl border border-line bg-surface p-4 shadow-pop" style={{ width }}>
        <div className="text-[15px] font-semibold">{title}</div>
        {note && <div className="mt-0.5 text-[12px] text-muted">{note}</div>}
        {children}
      </div>
    </DialogLayer>
  );
}

function Field({ label, children, span }: { label: string; children: ReactNode; span?: boolean }) {
  return (
    <label className={cx("flex flex-col gap-1 text-[12px]", span && "col-span-2")}>
      <span className="text-muted">{label}</span>
      {children}
    </label>
  );
}

const int = (s: string) => {
  const v = Math.round(numberOf(s));
  return Number.isFinite(v) && v >= 0 ? v : 0;
};

/**
 * One save at a time: a second click while the first is on its way does nothing (each dialog also keeps the id it made
 * when it opened, so a retry is the same record, never a second one).
 */
function useSaving() {
  const busy = useRef(false);
  const [saving, setSaving] = useState(false);
  const guard =
    <A extends unknown[]>(fn: (...a: A) => Promise<unknown>) =>
    async (...a: A) => {
      if (busy.current) return;
      busy.current = true;
      setSaving(true);
      try {
        await fn(...a);
      } finally {
        busy.current = false;
        setSaving(false);
      }
    };
  return { saving, guard };
}

function PresaleDialog({ runId, id, onClose }: { runId: string; id: string | null; onClose: () => void }) {
  const presales = useRows("presale");
  const existing: Presale | undefined = id ? presales.find((p) => p.id === id) : undefined;
  const [newId] = useState(() => crypto.randomUUID());
  const { saving, guard } = useSaving();
  const [d, setD] = useState(() => ({
    name: existing?.name ?? "",
    channels: (existing?.channels ?? ["kickstarter"]) as PresaleChannel[],
    status: existing?.status ?? "open",
    ships_from: existing?.ships_from ?? "cm",
    order_tag: existing?.order_tag ?? "",
    us: String(existing?.backers_us ?? 0),
    intl: String(existing?.backers_intl ?? 0),
    unknown: String(existing?.backers_unknown ?? 0),
    pledge: existing?.pledge_total == null ? "" : String(existing.pledge_total),
    notes: existing?.notes ?? "",
  }));
  const set = (patch: Partial<typeof d>) => setD({ ...d, ...patch });
  const save = guard(async () => {
    if (!d.name.trim()) return toast.error("Name it (e.g. GCU2 Kickstarter)");
    if (!d.channels.length) return toast.error("Pick at least one channel");
    const pledge = d.pledge.trim() === "" ? null : numberOf(d.pledge);
    if (pledge != null && !(Number.isFinite(pledge) && pledge >= 0)) return toast.error("The total pledge value is an amount in USD (or leave it empty)");
    const fields = {
      name: d.name.trim(),
      channels: d.channels,
      status: d.status,
      ships_from: d.ships_from,
      order_tag: d.order_tag.trim(),
      backers_us: int(d.us),
      backers_intl: int(d.intl),
      backers_unknown: int(d.unknown),
      pledge_total: pledge,
      notes: d.notes,
    };
    const ok = existing
      ? await mutate("presale.update", { id: existing.id, patch: fields })
      : await mutate("presale.create", { id: newId, run_id: runId, ...fields, as_of: planToday() });
    if (ok) onClose();
  });
  const remove = async () => {
    if (!existing || !confirm(`Delete ${existing.name} and its reservations?`)) return;
    if (await mutate("presale.remove", { id: existing.id })) onClose();
  };
  return (
    <Dialog
      title={existing ? existing.name : "Add a pre-sale"}
      note="A campaign on this run. Its units per item come from the backers app (presale-import) or are added by hand; never a backer's name or address."
      onClose={onClose}
    >
      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2">
        <Field label="Name" span>
          <Input value={d.name} placeholder="GCU2 Kickstarter" onChange={(e) => set({ name: e.target.value })} />
        </Field>
        <div className="col-span-2 flex flex-wrap gap-3 text-[12px]">
          {PRESALE_CHANNELS.map((c) => (
            <label key={c} className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={d.channels.includes(c)}
                onChange={(e) => set({ channels: e.target.checked ? [...d.channels, c] : d.channels.filter((x) => x !== c) })}
              />
              {CHANNEL_LABELS[c]}
            </label>
          ))}
        </div>
        <Field label="Status">
          <Select
            value={d.status}
            options={PRESALE_STATUSES.map((s) => ({ value: s, label: `${PRESALE_STATUS_LABELS[s]}: ${PRESALE_STATUS_HINTS[s]}` }))}
            onChange={(e) => set({ status: e.target.value as typeof d.status })}
          />
        </Field>
        <Field label="Ships from">
          <Select
            value={d.ships_from}
            options={(["cm", "hhl"] as const).map((v) => ({ value: v, label: SHIPS_FROM_LABELS[v] }))}
            onChange={(e) => set({ ships_from: e.target.value as typeof d.ships_from })}
          />
        </Field>
        <Field label="Backers: US">
          <Input value={d.us} inputMode="numeric" className="font-mono" onChange={(e) => set({ us: e.target.value })} />
        </Field>
        <Field label="Backers: international">
          <Input value={d.intl} inputMode="numeric" className="font-mono" onChange={(e) => set({ intl: e.target.value })} />
        </Field>
        <Field label="Backers: no country yet">
          <Input value={d.unknown} inputMode="numeric" className="font-mono" onChange={(e) => set({ unknown: e.target.value })} />
        </Field>
        <Field label="Shopify order tag (left out of sales history)">
          <Input value={d.order_tag} placeholder="kickstarter-gcu2" className="font-mono" onChange={(e) => set({ order_tag: e.target.value })} />
        </Field>
        <Field label="Total pledge value (USD, gross)">
          <Input value={d.pledge} inputMode="decimal" placeholder="Not known yet" className="font-mono" onChange={(e) => set({ pledge: e.target.value })} />
        </Field>
        <div className="col-span-2 text-[11px] text-faint">
          What all its shipping backers pledged, before the platforms' fees (the backers app's total payment). Each shipment's share of the deposits in the
          bookkeeper pack is its pledge value × deposits ÷ this.
        </div>
        <Field label="Notes" span>
          <Textarea value={d.notes} onChange={(e) => set({ notes: e.target.value })} className="min-h-14" />
        </Field>
      </div>
      <div className="mt-4 flex items-center gap-2">
        {existing && can("presale.remove") && (
          <Button variant="danger" onClick={remove}>
            Delete
          </Button>
        )}
        <div className="ml-auto flex gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save} disabled={saving}>
            {existing ? "Save" : "Add pre-sale"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function LineDialog({
  presaleId: initial,
  presales,
  itemId,
  runId,
  onClose,
}: {
  presaleId: string;
  presales: readonly Presale[];
  itemId: string | null;
  runId: string;
  onClose: () => void;
}) {
  const lines = useRows("presale_line");
  const items = useRows("item");
  const runLines = useRows("production_run_line");
  const [presaleId, setPresaleId] = useState(initial);
  const [newId] = useState(() => crypto.randomUUID());
  const { saving, guard } = useSaving();
  const [item, setItem] = useState(itemId ?? "");
  const lineOf = (p: string) => (item ? lines.find((l) => l.presale_id === p && l.item_id === item) : undefined);
  const existing = lineOf(presaleId);
  const fromLine = (l: typeof existing) => ({
    us: String(l?.us_qty ?? 0),
    intl: String(l?.intl_qty ?? 0),
    unknown: String(l?.unknown_qty ?? 0),
    assigned: String(l?.assigned_qty ?? 0),
    notShipping: String(l?.not_shipping_qty ?? 0),
    note: l?.note ?? "",
  });
  const [d, setD] = useState(() => fromLine(existing));
  const set = (patch: Partial<typeof d>) => setD({ ...d, ...patch });
  // Another pre-sale of the run: its own line for the item (or a new one).
  const pick = (p: string) => {
    setPresaleId(p);
    setD(fromLine(lineOf(p)));
  };
  const onRun = new Set(runLines.filter((l) => l.run_id === runId).map((l) => l.item_id));
  const options = items
    .filter((i) => !i.archived && (i.kind !== "service" || i.id === item))
    .sort((a, b) => Number(onRun.has(b.id)) - Number(onRun.has(a.id)) || a.sku.localeCompare(b.sku))
    .map((i) => ({ value: i.id, label: `${i.sku} · ${i.shopify_variant_title || i.name}` }));
  const save = guard(async () => {
    if (!item) return toast.error("Pick the item");
    const fields = {
      us_qty: int(d.us),
      intl_qty: int(d.intl),
      unknown_qty: int(d.unknown),
      assigned_qty: int(d.assigned),
      not_shipping_qty: int(d.notShipping),
      note: d.note,
    };
    const ok = existing
      ? await mutate("presale_line.update", { id: existing.id, patch: fields })
      : await mutate("presale_line.create", { id: newId, presale_id: presaleId, item_id: item, ...fields });
    if (ok) onClose();
  });
  const remove = guard(async () => {
    if (existing && (await mutate("presale_line.remove", { id: existing.id }))) onClose();
  });
  const reserved = int(d.us) + int(d.intl) + int(d.unknown);
  const name = presales.find((p) => p.id === presaleId)?.name ?? "the pre-sale";
  return (
    <Dialog
      title={existing ? `Reserved units: ${name}` : `Reserve an item: ${name}`}
      note="Units promised to backers. For a part that's also in the run's builds, only the add-on units."
      onClose={onClose}
    >
      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2">
        {presales.length > 1 && (
          <Field label="For which pre-sale" span>
            <Select
              value={presaleId}
              options={presales.map((p) => ({
                value: p.id,
                label: `${p.name}${p.status === "cancelled" ? " (cancelled)" : ""}${lineOf(p.id) ? "" : item ? ": doesn't reserve it yet" : ""}`,
              }))}
              onChange={(e) => pick(e.target.value)}
            />
          </Field>
        )}
        <Field label="Item" span>
          <Select
            value={item}
            options={[{ value: "", label: "Pick the item…" }, ...options]}
            onChange={(e) => {
              setItem(e.target.value);
              setD(fromLine(lines.find((l) => l.presale_id === presaleId && l.item_id === e.target.value)));
            }}
            disabled={!!itemId}
          />
        </Field>
        <Field label="US backers">
          <Input value={d.us} inputMode="numeric" className="font-mono" onChange={(e) => set({ us: e.target.value })} />
        </Field>
        <Field label="International backers">
          <Input value={d.intl} inputMode="numeric" className="font-mono" onChange={(e) => set({ intl: e.target.value })} />
        </Field>
        <Field label="No country yet">
          <Input value={d.unknown} inputMode="numeric" className="font-mono" onChange={(e) => set({ unknown: e.target.value })} />
        </Field>
        <Field label="Of them, colours HHL assigned">
          <Input value={d.assigned} inputMode="numeric" className="font-mono" onChange={(e) => set({ assigned: e.target.value })} />
        </Field>
        <Field label="Not shipping (info only)">
          <Input value={d.notShipping} inputMode="numeric" className="font-mono" onChange={(e) => set({ notShipping: e.target.value })} />
        </Field>
        <div className="flex items-end pb-1.5 text-[12px] text-muted">
          Reserved: <span className="ml-1 font-mono text-text">{qty(reserved)}</span>
        </div>
        <Field label="Note" span>
          <Input value={d.note} onChange={(e) => set({ note: e.target.value })} />
        </Field>
      </div>
      <div className="mt-4 flex items-center gap-2">
        {existing && (
          <Button variant="danger" onClick={remove}>
            Take it off
          </Button>
        )}
        <div className="ml-auto flex gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save} disabled={saving}>
            Save
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function HoldDialog({ runId, id, itemId, rows, onClose }: { runId: string; id: string | null; itemId?: string; rows: RunItemRow[]; onClose: () => void }) {
  const holds = useRows("production_run_hold");
  const runLines = useRows("production_run_line");
  const items = useRows("item");
  const existing: ProductionRunHold | undefined = id ? holds.find((h) => h.id === id) : undefined;
  const [item, setItem] = useState(existing?.item_id ?? itemId ?? "");
  const [count, setCount] = useState(String(existing?.qty ?? ""));
  const [reason, setReason] = useState(existing?.reason ?? "gifts, influencers, employees");
  const [newId] = useState(() => crypto.randomUUID());
  const { saving, guard } = useSaving();
  const ids = new Set([...runLines.filter((l) => l.run_id === runId).map((l) => l.item_id), ...rows.map((r) => r.itemId)]);
  const options = items
    .filter((i) => ids.has(i.id))
    .sort((a, b) => a.sku.localeCompare(b.sku))
    .map((i) => ({ value: i.id, label: `${i.sku} · ${i.shopify_variant_title || i.name}` }));
  const save = guard(async () => {
    if (!item) return toast.error("Pick the item");
    const ok = existing
      ? await mutate("production_run_hold.update", { id: existing.id, patch: { qty: int(count), reason } })
      : await mutate("production_run_hold.create", { id: newId, run_id: runId, item_id: item, qty: int(count), reason });
    if (ok) onClose();
  });
  const remove = guard(async () => {
    if (existing && (await mutate("production_run_hold.remove", { id: existing.id }))) onClose();
  });
  return (
    <Dialog
      title={existing ? "Held units" : "Hold units"}
      note="Units of this run set aside, not sold. They arrive at HHL and go into stock as usual, but planning never counts them as for sale. Lower or remove the hold to release them."
      onClose={onClose}
    >
      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2">
        <Field label="Item" span>
          <Select value={item} options={[{ value: "", label: "Pick the item…" }, ...options]} onChange={(e) => setItem(e.target.value)} disabled={!!existing} />
        </Field>
        <Field label="Units held">
          <Input value={count} inputMode="numeric" className="font-mono" onChange={(e) => setCount(e.target.value)} />
        </Field>
        <Field label="Why">
          <Input value={reason} placeholder="gifts, influencers, employees" onChange={(e) => setReason(e.target.value)} />
        </Field>
      </div>
      <div className="mt-4 flex items-center gap-2">
        {existing && (
          <Button variant="danger" onClick={remove}>
            Release all
          </Button>
        )}
        <div className="ml-auto flex gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save} disabled={saving}>
            Save
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function IncomeDialog({ presaleId, id, onClose }: { presaleId: string; id: string | null; onClose: () => void }) {
  const rows = useRows("presale_income");
  const existing: PresaleIncome | undefined = id ? rows.find((e) => e.id === id) : undefined;
  const [entryId] = useState(() => id ?? crypto.randomUUID());
  const [saved, setSaved] = useState(!!existing);
  const [d, setD] = useState(() => ({
    source: existing?.source ?? "kickstarter",
    kind: existing?.kind ?? ("payout" as IncomeKind),
    received_on: existing ? String(existing.received_on).slice(0, 10) : planToday(),
    amount: existing ? String(existing.amount) : "",
    currency: existing?.currency ?? "USD",
    rate: existing && existing.currency !== "USD" ? String(existing.usd_rate) : "",
    reference: existing?.reference ?? "",
    description: existing?.description ?? "",
    note: existing?.note ?? "",
  }));
  const set = (patch: Partial<typeof d>) => setD({ ...d, ...patch });
  const { saving, guard } = useSaving();
  const usd = d.currency.toUpperCase() === "USD";
  const save = guard(async () => {
    const amount = numberOf(d.amount);
    if (!Number.isFinite(amount) || d.amount.trim() === "") return toast.error("Enter the amount");
    if (!usd && !(numberOf(d.rate) > 0)) return toast.error(`Enter the USD rate for ${d.currency}`);
    const fields = {
      source: d.source,
      kind: d.kind,
      received_on: d.received_on,
      amount,
      currency: d.currency.toUpperCase(),
      usd_rate: usd ? 1 : numberOf(d.rate),
      reference: d.reference,
      description: d.description,
      note: d.note,
    };
    if (saved) {
      if (await mutate("presale_income.update", { id: entryId, patch: fields })) onClose();
      return;
    }
    if (await mutate("presale_income.create", { id: entryId, presale_id: presaleId, ...fields })) {
      setSaved(true);
      toast.ok("Added: drop its payout statement below");
    }
  });
  const remove = async () => {
    if (!confirm("Delete this entry and its files?")) return;
    if (await mutate("presale_income.remove", { id: entryId })) onClose();
  };
  return (
    <Dialog
      title={existing ? "Income entry" : "Add income"}
      note="Use the amounts as paid out. A payout's fees are already off it: enter fees on their own only when they came out separately (PledgeBox's)."
      onClose={onClose}
    >
      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2">
        <Field label="From">
          <Select
            value={d.source}
            options={INCOME_SOURCES.map((s) => ({ value: s, label: INCOME_SOURCE_LABELS[s] }))}
            onChange={(e) => set({ source: e.target.value as typeof d.source })}
          />
        </Field>
        <Field label="What">
          <Select
            value={d.kind}
            options={INCOME_KINDS.map((k) => ({ value: k, label: `${INCOME_KIND_LABELS[k]}${INCOME_OUT.has(k) ? " (money out)" : ""}` }))}
            onChange={(e) => set({ kind: e.target.value as IncomeKind })}
          />
        </Field>
        <Field label="Date">
          <Input type="date" value={d.received_on} onChange={(e) => set({ received_on: e.target.value })} />
        </Field>
        <Field label="Amount">
          <div className="flex gap-1.5">
            <Input value={d.amount} inputMode="decimal" placeholder="0.00" className="font-mono" onChange={(e) => set({ amount: e.target.value })} />
            <UpperInput value={d.currency} maxLength={3} className="w-16 font-mono" aria-label="Currency" onText={(currency) => set({ currency })} />
          </div>
        </Field>
        {!usd && (
          <Field label={`USD per 1 ${d.currency}`}>
            <Input value={d.rate} inputMode="decimal" className="font-mono" onChange={(e) => set({ rate: e.target.value })} />
          </Field>
        )}
        <Field label="Description">
          <Input value={d.description} placeholder="Campaign, Pledge Over Time, Late Pledge…" onChange={(e) => set({ description: e.target.value })} />
        </Field>
        <Field label="Reference">
          <Input value={d.reference} placeholder="The payout's reference" className="font-mono" onChange={(e) => set({ reference: e.target.value })} />
        </Field>
        <Field label="Note" span>
          <Input value={d.note} onChange={(e) => set({ note: e.target.value })} />
        </Field>
        <div className="col-span-2 text-[11px] text-faint">{INCOME_KIND_HINTS[d.kind]}</div>
      </div>
      <div className="mt-3">
        <div className="mb-1 text-[12px] font-medium">Payout statement</div>
        {saved ? (
          <Attachments owner={{ type: "presale_income", id: entryId }} kind="receipt" hint={PRESALE_FILE_HINT} />
        ) : (
          <div className="text-[12px] text-faint">Save it first, then drop the statement here.</div>
        )}
      </div>
      <div className="mt-4 flex items-center gap-2">
        {saved && (
          <Button variant="danger" onClick={remove}>
            Delete
          </Button>
        )}
        <div className="ml-auto flex gap-2">
          <Button variant="ghost" onClick={onClose}>
            {saved && !existing ? "Done" : "Cancel"}
          </Button>
          <Button variant="primary" onClick={save} disabled={saving}>
            {saved ? "Save" : "Add"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function ShipmentDialog({ presaleId, rows, onClose }: { presaleId: string; rows: RunItemRow[]; onClose: () => void }) {
  const [d, setD] = useState(() => ({
    shipped_on: planToday(),
    region: "us" as Region,
    packages: "",
    pledge: "",
    note: "",
    units: Object.fromEntries(rows.map((r) => [r.itemId, ""])) as Record<string, string>,
    costs: Object.fromEntries(SHIPPING_KINDS.map((k) => [k, ""])) as Record<ShippingKind, string>,
  }));
  const set = (patch: Partial<typeof d>) => setD({ ...d, ...patch });
  const unitRows = rows.map((r) => ({ ...r, id: r.itemId }));
  const unitColumns: Column<(typeof unitRows)[number]>[] = [
    { key: "sku", header: "SKU", width: "minmax(110px,1fr)", cell: (r) => <span className="truncate font-mono text-[12px]">{r.sku}</span> },
    { key: "name", header: "Name", width: "minmax(130px,1.4fr)", cell: (r) => <span className="truncate text-[12px] text-muted">{r.name}</span> },
    {
      key: "remaining",
      header: "Left",
      headerTitle: "Reserved and not shipped yet",
      width: "80px",
      align: "right",
      text: (r) => r.remaining,
      cell: (r) => <span className="text-[12px] text-faint">{qty(r.remaining)} left</span>,
    },
    {
      key: "units",
      header: "Units",
      width: "90px",
      align: "right",
      value: (r) => d.units[r.itemId] ?? "",
      cell: (r) => (d.units[r.itemId]?.trim() ? <span className="font-mono text-[12px]">{d.units[r.itemId]}</span> : <span className="text-faint">0</span>),
      // Each save takes the dialog as it is then, so units typed one after another all stay.
      edit: { get: (r) => d.units[r.itemId] ?? "", save: (r, text) => setD((was) => ({ ...was, units: { ...was.units, [r.itemId]: text } })) },
    },
  ];
  // One id per dialog: a double click, or a retry after a lost answer, records the same shipment (a no-op the second time).
  const [id] = useState(() => crypto.randomUUID());
  const { saving, guard } = useSaving();
  const send = async (confirmOver: boolean): Promise<void> => {
    const lines = rows.flatMap((r) => (int(d.units[r.itemId] ?? "") > 0 ? [{ item_id: r.itemId, qty: int(d.units[r.itemId]) }] : []));
    if (!lines.length) {
      toast.error("Say what was shipped");
      return;
    }
    const res = await fetch("/api/mutate", {
      method: "POST",
      headers: { "content-type": "application/json", "x-requested-with": "hhl-ops" },
      body: JSON.stringify({
        name: "presale_shipment.record",
        args: {
          id,
          presale_id: presaleId,
          shipped_on: d.shipped_on,
          region: d.region,
          packages: int(d.packages),
          pledge_value: numberOf(d.pledge) || 0,
          note: d.note,
          lines,
          costs: SHIPPING_KINDS.flatMap((k) => {
            const amount = numberOf(d.costs[k]);
            return amount ? [{ kind: k, amount, currency: "USD" }] : [];
          }),
          confirm_over: confirmOver,
        },
      }),
    });
    const body = (await res.json().catch(() => ({}))) as { error?: string; result?: { notes?: string[]; already?: boolean } };
    if (res.status === 409 && !confirmOver) {
      if (confirm(`${body.error}`)) return send(true);
      return;
    }
    if (!res.ok) {
      toast.error(body.error ?? "Couldn't record the shipment");
      return;
    }
    for (const note of body.result?.notes ?? []) toast.info(note);
    toast.ok(body.result?.already ? "Already recorded" : "Shipment recorded");
    onClose();
  };
  const save = guard(() => send(false));
  return (
    <Dialog
      title="Record a shipment to backers"
      note="A dated total, usually a month's per region. Its units leave the CM; its shipping costs go on the run as Shipping to backers."
      onClose={onClose}
      width={620}
    >
      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2">
        <Field label="Shipped on (or the month's last day)">
          <Input type="date" value={d.shipped_on} onChange={(e) => set({ shipped_on: e.target.value })} />
        </Field>
        <Field label="Region">
          <Select
            value={d.region}
            options={REGIONS.map((r) => ({ value: r, label: REGION_LABELS[r] }))}
            onChange={(e) => set({ region: e.target.value as Region })}
          />
        </Field>
        <Field label="Packages">
          <Input value={d.packages} inputMode="numeric" className="font-mono" onChange={(e) => set({ packages: e.target.value })} />
        </Field>
        <Field label="Pledge value (USD): what these backers paid">
          <Input value={d.pledge} inputMode="decimal" className="font-mono" onChange={(e) => set({ pledge: e.target.value })} />
        </Field>
      </div>
      <div className="mt-3 mb-1 flex items-center gap-2">
        <span className="text-[12px] font-medium">Units</span>
      </div>
      {/* Click a row's units to type them; Enter saves and goes on to the next item */}
      <DataTable fit={7} editing rows={unitRows} columns={unitColumns} exportName="Shipment units" pageFilters={false} rowNoun={["item", "items"]} />
      <div className="mt-3 text-[12px] font-medium">Shipping costs (USD)</div>
      <div className="mt-1 grid grid-cols-5 gap-2">
        {SHIPPING_KINDS.map((k) => (
          <Field key={k} label={k === "freight" ? "Freight (Philly)" : k === "other" ? "Other" : SHIPPING_KIND_LABELS[k]}>
            <Input value={d.costs[k]} inputMode="decimal" className="font-mono" onChange={(e) => set({ costs: { ...d.costs, [k]: e.target.value } })} />
          </Field>
        ))}
      </div>
      <div className="mt-2">
        <Field label="Note">
          <Input value={d.note} onChange={(e) => set({ note: e.target.value })} />
        </Field>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => save()} disabled={saving}>
          {saving ? "Recording…" : "Record it"}
        </Button>
      </div>
    </Dialog>
  );
}
